import Link from 'next/link';
import PropTypes from 'prop-types';
import { css, cx } from 'linaria';
import { Trans, useTranslation } from 'next-i18next';
import { PremiumIcon } from 'components/icons/PremiumIcon';
import { MenuIcon } from 'components/icons/MenuIcon';
import { ChevronLeftIcon } from 'components/icons/ChevronLeftIcon';
import { Drawer } from 'components/drawer/drawer';
import { Button } from 'components/buttons/button';
import { DEFAULT_LINKS } from 'components/global-nav/links/global-nav-links';
import { bottomTooltip } from 'components/tooltip/tooltip';
const premiumLinks = [{
  name: 'upgrade',
  id: 'upgrade-to-premium',
  label: <Trans i18nKey="nav:upgrade">Upgrade</Trans>,
  url: 'https://getpocket.com/premium?src=navbar',
  icon: <PremiumIcon />
}];
const listStyle = "lg0uqr9";
const iconStyle = "i1oaez37";
export const MobileLink = ({
  link: {
    name,
    isDisabled = false,
    url,
    icon,
    label,
    id
  },
  isSelected,
  handleClick
}) => <li>
    <Link href={isDisabled ? null : url}>
      <a id={id} className={cx(isSelected && 'selected', isDisabled && 'disabled')} onClick={event => {
      handleClick(event, name, url);
    }}>
        {icon ? icon : null}
        {label}
      </a>
    </Link>
  </li>;
const drawerHeaderStyles = "dokzgub";
const DrawerHeader = ({
  handleClose
}) => {
  const {
    t
  } = useTranslation();
  return <div className={drawerHeaderStyles}>
      <Button aria-label={t('nav:close-the-pocket-mobile-menu', 'Close the Pocket mobile menu')} data-cy="mobile-menu" data-tooltip={t('nav:close', 'Close')} onClick={handleClose} variant="inline" className={cx(iconStyle, bottomTooltip)}>
        <ChevronLeftIcon />
      </Button>
    </div>;
};
export const Menu = ({
  links,
  subLinks,
  selectedLink,
  isUserLoggedIn,
  isUserPremium,
  handleClick
}) => <ul className={listStyle}>
    {links.map(link => {
    const isSelected = link.name === selectedLink;
    const key = `global-nav-mobile-menu-${link?.name}`;
    return <MobileLink link={link} isSelected={isSelected} handleClick={event => {
      handleClick(event, link.name, link.url);
    }} key={key} />;
  })}
    {subLinks ? subLinks.map(link => {
    const key = `global-nav-mobile-menu-${link?.name}`;
    return link.url ? <MobileLink link={link} handleClick={event => {
      handleClick(event, link.name, link.url);
    }} key={key} /> : null;
  }) : null}
    {isUserLoggedIn && !isUserPremium ? <>
        <hr className="nav-divider" />
        <span className="subhead" data-cy="premium-nudge-section">
          <Trans i18nKey="nav:try-premium">Try Premium</Trans>
        </span>
        {premiumLinks.map(premiumLink => {
      const isSelected = premiumLink.name === selectedLink;
      return <MobileLink link={premiumLink} isSelected={isSelected} handleClick={handleClick} key={`global-nav-mobile-menu-${premiumLink?.name}`} />;
    })}
      </> : null}
  </ul>;

/**
 * Component to render navigational links in the GlobalNav. Accepts a list of links
 * to render so that links may be customized per page context.
 */
const GlobalNavMobileMenu = ({
  links,
  subLinks,
  selectedLink,
  onLinkClick,
  isUserLoggedIn,
  isUserPremium,
  onOpen,
  onClosed,
  appRootSelector,
  isOpen,
  toggleMenuOpen,
  toggleClass
}) => {
  const {
    t
  } = useTranslation();
  function handleClick(event, linkName, linkUrl) {
    onLinkClick(linkName, linkUrl);
  }
  const handleClose = () => {
    onClosed();
    toggleMenuOpen(false);
  };
  const handleOpen = () => {
    onOpen();
    toggleMenuOpen(true);
  };
  return <>
      <Button data-cy="nav-hamburger" onClick={handleOpen} aria-label={t('nav:open-the-pocket-mobile-menu', 'Open the Pocket mobile menu')} data-tooltip={t('nav:open', 'Open')} variant="inline" className={cx(iconStyle, toggleClass, bottomTooltip)}>
        <MenuIcon />
      </Button>
      <Drawer appRootSelector={appRootSelector} isOpen={isOpen} handleClose={handleClose} screenReaderLabel={t('nav:pocket-mobile-menu', 'Pocket Mobile Menu')}>
        <DrawerHeader handleClose={handleClose} />
        <Menu subLinks={subLinks} links={links} selectedLink={selectedLink} handleClick={handleClick} isUserPremium={isUserPremium} isUserLoggedIn={isUserLoggedIn} />
      </Drawer>
    </>;
};
GlobalNavMobileMenu.propTypes = {
  /**
   * query selector specifying root element of React App (e.g. '#root'). This is
   * where the Drawer markup will be injected.
   */
  appRootSelector: PropTypes.string.isRequired,
  /**
   * Links to display. Accepted as a prop so that links can be customized per page
   * context via the GlobalNav parent. Each link should have a name (id), label
   * (for display), and url. Each link will be rendered as an MobileLink in a menu.
   */
  links: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    url: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    icon: PropTypes.node
  })),
  /**
   * Name of a link that is selected, if any. Should match the name of one of the
   * links passed in with props. If a link matches this name, it will have an
   * "selected" style applied.
   */
  selectedLink: PropTypes.string,
  /**
   * Callback function called when any link is clicked. Gets passed the name and
   * url of that link.
   */
  onLinkClick: PropTypes.func,
  /**
   * Callback function called when the mobile menu is opened. Useful for analytics
   */
  onOpen: PropTypes.func,
  /**
   * Callback function called when the mobile menu is closed. Useful for analytics
   */
  onClosed: PropTypes.func,
  /**
   * Boolean indicating whether the user is logged in
   * url of that link.
   */
  isUserLoggedIn: PropTypes.bool,
  /**
   * Boolean indicating whether the user is a Premium User
   */
  isUserPremium: PropTypes.bool,
  /**
   * Boolean targeting dev users and tests, to provide an initial open state
   * for mobile menu
   */
  forceShow: PropTypes.bool
};
GlobalNavMobileMenu.defaultProps = {
  links: DEFAULT_LINKS,
  selectedLink: null,
  onLinkClick() {},
  onOpen() {},
  onClosed() {},
  isUserLoggedIn: false,
  isUserPremium: false,
  forceShow: false
};
export default GlobalNavMobileMenu;

require("./global-nav-mobile-menu.linaria.module.css!=!../../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./global-nav-mobile-menu.js");